import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { colorOf, DAYS, trUpper, MONTHS } from "@/lib/subjects";
import { toast } from "sonner";
import { BookMarked, Printer, Clock, CheckCircle2, ShieldCheck, Pencil, Trash2, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const STATUS = {
  bekliyor: { cell: "bg-white", label: "Bekliyor", chip: "bg-white border-slate-300", icon: Clock, iconColor: "text-amber-500" },
  tamamlandi: { cell: "bg-amber-50 border-amber-200", label: "Tamamlandı", chip: "bg-amber-100 border-amber-300", icon: CheckCircle2, iconColor: "text-amber-600" },
  onayli: { cell: "bg-emerald-50 border-emerald-200", label: "Koç Onaylı", chip: "bg-emerald-100 border-emerald-300", icon: ShieldCheck, iconColor: "text-emerald-600" },
};

export default function StudyProgram() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [studentId, setStudentId] = useState(params.get("student") || null);
  const [studentName, setStudentName] = useState(params.get("name") || "");
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null); // {week_number, row_index, day, cell, subject}
  const [programs, setPrograms] = useState([]);
  const [activePid, setActivePid] = useState(null);

  const canEdit = user?.role !== "veli";
  const canApprove = user?.role === "admin" || user?.role === "koc";
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (studentId) return;
    api.get("/my/context").then(({ data }) => {
      setStudentId(data.student_id);
      setStudentName(data.student_name || "");
    });
  }, [studentId]);

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/study-program", { params: { student_id: studentId, ...(activePid ? { program_id: activePid } : {}) } });
      setProgram(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [studentId, activePid]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (studentId) api.get("/study-programs/list", { params: { student_id: studentId } }).then(({ data }) => setPrograms(data)).catch(() => {});
  }, [studentId]);

  const saveCell = async () => {
    try {
      const { data } = await api.patch("/study-program/cell", {
        student_id: studentId,
        week_number: edit.week_number,
        row_index: edit.row_index,
        day: edit.day,
        dogru: Number(edit.cell.dogru) || 0,
        yanlis: Number(edit.cell.yanlis) || 0,
        bos: Number(edit.cell.bos) || 0,
        status: edit.cell.status,
        program_id: program?.id || activePid || undefined,
        ...(isAdmin ? { unit: edit.cell.unit || "", pages: edit.cell.pages || "", target: Number(edit.cell.target) || 0 } : {}),
      });
      setProgram(data);
      setEdit(null);
      toast.success("Kaydedildi ✓");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const clearCell = async () => {
    try {
      const { data } = await api.patch("/study-program/cell", {
        student_id: studentId, week_number: edit.week_number, row_index: edit.row_index, day: edit.day, clear: true, program_id: program?.id || activePid || undefined,
      });
      setProgram(data);
      setEdit(null);
      toast.success("Görev silindi");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const approveCell = async (week_number, row_index, day, cell) => {
    try {
      const { data } = await api.patch("/study-program/cell", {
        student_id: studentId, week_number, row_index, day,
        dogru: cell.dogru || 0, yanlis: cell.yanlis || 0, bos: cell.bos || 0, status: "onayli",
        program_id: program?.id || activePid || undefined,
      });
      setProgram(data);
      toast.success("Koç onayı verildi ✓");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const goEdit = () => nav(`/admin/calisma-programi?student=${studentId}${studentName ? `&name=${encodeURIComponent(studentName)}` : ""}`);

  const deleteProgram = async () => {
    if (!window.confirm(program?.id ? `"${program?.title || "Seçili"}" programı silinsin mi?` : "Bu öğrencinin çalışma programı tamamen silinsin mi?")) return;
    try {
      await api.delete("/study-program", { params: { student_id: studentId, ...(program?.id ? { program_id: program.id } : {}) } });
      toast.success("Program silindi");
      setActivePid(null);
      const { data: lst } = await api.get("/study-programs/list", { params: { student_id: studentId } });
      setPrograms(lst);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const persistWeeks = async (weeks) => {
    try {
      const { data } = await api.post("/study-program", { student_id: studentId, title: program?.title || "", weeks, program_id: program?.id || activePid || undefined });
      setProgram(data);
      if (data?.id && !activePid) setActivePid(data.id);
      return true;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return false; }
  };

  const addWeek = async () => {
    const weeks = (program?.weeks || []).map((w) => ({ ...w }));
    const next = weeks.length ? Math.max(...weeks.map((w) => w.week_number || 0)) + 1 : 1;
    weeks.push({ week_number: next, title: "", month: weeks.length ? weeks[weeks.length - 1].month || "" : "", rows: [] });
    if (await persistWeeks(weeks)) toast.success(`${next}. hafta eklendi — düzenlemek için "Düzenle"ye gidin`);
  };

  const deleteWeek = async (week_number) => {
    if (!window.confirm(`${week_number}. hafta silinsin mi?`)) return;
    const weeks = (program?.weeks || []).filter((w) => w.week_number !== week_number);
    if (await persistWeeks(weeks)) toast.success("Hafta silindi");
  };

  const rowSave = async (week_number, ri, patch) => {
    const weeks = (program?.weeks || []).map((w) => (w.week_number !== week_number ? w : { ...w, rows: w.rows.map((r, i) => (i === ri ? { ...r, ...patch } : r)) }));
    if (await persistWeeks(weeks)) toast.success("Kaydedildi ✓");
  };

  const rowDelete = async (week_number, ri) => {
    if (!window.confirm("Bu ders satırı silinsin mi?")) return;
    const weeks = (program?.weeks || []).map((w) => (w.week_number !== week_number ? w : { ...w, rows: w.rows.filter((_, i) => i !== ri) }));
    if (await persistWeeks(weeks)) toast.success("Satır silindi");
  };

  const editWeek = async (week_number, patch) => {
    const weeks = (program?.weeks || []).map((w) => (w.week_number !== week_number ? w : { ...w, ...patch }));
    if (await persistWeeks(weeks)) toast.success("Hafta güncellendi ✓");
  };

  if (!studentId) {
    return <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500">Bağlı öğrenci bulunamadı.</div>;
  }

  return (
    <div className="mx-auto max-w-[1400px] space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="mb-1 flex items-center gap-2 text-slate-500"><BookMarked className="h-5 w-5" /> Kaynak Bazlı Haftalık Plan</div>
          <h1 className="font-display text-4xl font-extrabold text-slate-900">Çalışma Programı</h1>
          <p className="mt-1 text-slate-500">Kutucuğa tıklayıp doğru / yanlış / boş gir ve durumu işaretle.{studentName ? ` · ${studentName}` : ""}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3 no-print">
          <Legend />
          {isAdmin && (
            <>
              <button data-testid="study-view-edit-btn" onClick={goEdit} className="flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 font-semibold text-white shadow-sm transition hover:bg-indigo-700 active:scale-95">
                <Pencil className="h-5 w-5" /> Düzenle / Ekle
              </button>
              <button data-testid="study-view-delete-btn" onClick={deleteProgram} className="flex items-center gap-2 rounded-xl bg-rose-50 px-4 py-2.5 font-semibold text-rose-600 transition hover:bg-rose-100 active:scale-95">
                <Trash2 className="h-5 w-5" /> Programı Sil
              </button>
            </>
          )}
          <button data-testid="print-program-btn" onClick={() => window.print()} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 font-semibold text-slate-700 shadow-sm transition hover:shadow-md active:scale-95">
            <Printer className="h-5 w-5" /> Yazdır / PDF
          </button>
        </div>
      </div>

      {/* Program title line (fotoğraftaki "HIZ HİBRİT · N hafta" gibi) */}
      {program?.weeks?.length ? (
        <div data-testid="program-title" className="flex flex-wrap items-center gap-2">
          <span className="font-display text-2xl font-extrabold tracking-wide text-indigo-700">{trUpper(program.title || "Çalışma Programı")}</span>
          {studentName && (<><span className="text-slate-300">·</span><span className="text-base font-semibold text-slate-500">{studentName}</span></>)}
          <span className="text-slate-300">·</span>
          <span className="text-base font-semibold text-slate-500">{program.weeks.length} hafta</span>
        </div>
      ) : null}

      {programs.length > 1 && (
        <div className="no-print flex flex-wrap items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Program:</span>
          {programs.map((p) => (
            <button key={p.id} data-testid={`view-program-chip-${p.id}`} onClick={() => setActivePid(p.id)} className={`rounded-full px-4 py-1.5 text-xs font-bold transition active:scale-95 ${p.id === program?.id ? "bg-indigo-600 text-white shadow" : "border border-slate-200 bg-white text-slate-600 hover:border-indigo-300 hover:text-indigo-600"}`}>
              {p.title || "Başlıksız"} · {p.week_count} hf
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="h-64 animate-pulse rounded-2xl bg-slate-200/60" />
      ) : !program || !program.weeks?.length ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center">
          <BookMarked className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-3 font-semibold text-slate-700">Henüz çalışma programı oluşturulmadı</p>
          <p className="text-sm text-slate-500">Yönetici program eklediğinde burada görünecek.</p>
        </div>
      ) : (
        <>
          {program.weeks.map((week) => <WeekTable key={week.week_number} week={week} canEdit={canEdit} onCell={setEdit} canApprove={canApprove} onApprove={approveCell} isAdmin={isAdmin} onDeleteWeek={deleteWeek} onRowSave={rowSave} onRowDelete={rowDelete} onEditWeek={editWeek} />)}
          {isAdmin && (
            <button data-testid="study-view-add-week-btn" onClick={addWeek} className="no-print flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-300 bg-white py-4 font-bold text-slate-500 transition hover:border-indigo-400 hover:text-indigo-600 active:scale-[0.99]">
              <Plus className="h-5 w-5" /> Yeni Hafta Ekle
            </button>
          )}
        </>
      )}

      {/* Cell edit dialog */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {edit?.subject} · {edit?.day}
            </DialogTitle>
          </DialogHeader>
          {edit && (
            <div className="space-y-4">
              {isAdmin ? (
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="mb-1 block text-center text-xs font-bold uppercase text-slate-500">Ünite</label>
                    <input data-testid="cell-unit-input" value={edit.cell.unit || ""} onChange={(e) => setEdit((x) => ({ ...x, cell: { ...x.cell, unit: e.target.value } }))} className="w-full rounded-xl border border-slate-200 px-2 py-2 text-center text-sm font-semibold outline-none focus:border-indigo-400" />
                  </div>
                  <div>
                    <label className="mb-1 block text-center text-xs font-bold uppercase text-slate-500">Sayfa</label>
                    <input data-testid="cell-pages-input" value={edit.cell.pages || ""} onChange={(e) => setEdit((x) => ({ ...x, cell: { ...x.cell, pages: e.target.value } }))} className="w-full rounded-xl border border-slate-200 px-2 py-2 text-center text-sm font-semibold outline-none focus:border-indigo-400" />
                  </div>
                  <div>
                    <label className="mb-1 block text-center text-xs font-bold uppercase text-slate-500">Hedef</label>
                    <input data-testid="cell-target-input" type="number" min={0} value={edit.cell.target || ""} onChange={(e) => setEdit((x) => ({ ...x, cell: { ...x.cell, target: e.target.value } }))} className="w-full rounded-xl border border-slate-200 px-2 py-2 text-center text-sm font-semibold outline-none focus:border-indigo-400" />
                  </div>
                </div>
              ) : (
                <div className="rounded-xl bg-slate-50 p-3 text-sm text-slate-600">
                  {edit.cell.unit} · {edit.cell.pages ? `Sayfa ${edit.cell.pages} · ` : ""}Hedef {edit.cell.target} soru
                </div>
              )}
              <div className="grid grid-cols-3 gap-3">
                <NumField label="Doğru" color="text-emerald-600" value={edit.cell.dogru} onChange={(v) => setEdit((e) => ({ ...e, cell: { ...e.cell, dogru: v } }))} testid="cell-dogru" />
                <NumField label="Yanlış" color="text-rose-600" value={edit.cell.yanlis} onChange={(v) => setEdit((e) => ({ ...e, cell: { ...e.cell, yanlis: v } }))} testid="cell-yanlis" />
                <NumField label="Boş" color="text-slate-500" value={edit.cell.bos} onChange={(v) => setEdit((e) => ({ ...e, cell: { ...e.cell, bos: v } }))} testid="cell-bos" />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">Durum</label>
                <div className="flex gap-2">
                  {["bekliyor", "tamamlandi", ...(canApprove ? ["onayli"] : [])].map((s) => (
                    <button
                      key={s}
                      data-testid={`cell-status-${s}`}
                      onClick={() => setEdit((e) => ({ ...e, cell: { ...e.cell, status: s } }))}
                      className={`flex-1 rounded-xl border px-3 py-2 text-sm font-semibold transition active:scale-95 ${edit.cell.status === s ? STATUS[s].chip + " text-slate-900" : "border-slate-200 bg-white text-slate-500"}`}
                    >
                      {STATUS[s].label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <div className="flex w-full gap-2">
              {isAdmin && (
                <button data-testid="delete-cell-btn" onClick={clearCell} className="flex items-center justify-center gap-2 rounded-xl bg-rose-50 px-5 py-3 font-bold text-rose-600 transition hover:bg-rose-100 active:scale-95">
                  <Trash2 className="h-5 w-5" /> Görevi Sil
                </button>
              )}
              <button data-testid="save-cell-btn" onClick={saveCell} className="flex-1 rounded-xl bg-indigo-600 py-3 font-bold text-white transition hover:bg-indigo-700 active:scale-95">Kaydet</button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function WeekTable({ week, canEdit, onCell, canApprove, onApprove, isAdmin, onDeleteWeek, onRowSave, onRowDelete, onEditWeek }) {
  const [wEditing, setWEditing] = useState(false);
  const [wMonth, setWMonth] = useState(week.month || "");
  const [wTitle, setWTitle] = useState(week.title || "");
  const saveWeek = () => { setWEditing(false); onEditWeek(week.week_number, { month: wMonth, title: wTitle }); };
  const total = week.rows.reduce((s, r) => s + Object.values(r.cells || {}).length, 0);
  const done = week.rows.reduce((s, r) => s + Object.values(r.cells || {}).filter((c) => c.status !== "bekliyor").length, 0);
  return (
    <div data-testid={`study-week-${week.week_number}`} className="study-print-week overflow-hidden rounded-2xl border border-slate-200 shadow-sm">
      <div className="flex items-center justify-between bg-[#1E293B] px-5 py-3.5 text-white">
        {wEditing ? (
          <div className="flex flex-wrap items-center gap-2">
            <select data-testid={`week-month-edit-${week.week_number}`} value={wMonth} onChange={(e) => setWMonth(e.target.value)} className="rounded-lg bg-white/10 px-2 py-1 text-sm text-white outline-none focus:ring-1 focus:ring-indigo-300">
              <option value="" className="text-slate-900">Ay yok</option>
              {MONTHS.map((m) => <option key={m} value={m} className="text-slate-900">{m}</option>)}
            </select>
            <input
              data-testid={`week-title-edit-${week.week_number}`}
              value={wTitle}
              onChange={(e) => setWTitle(e.target.value)}
              placeholder="Hafta başlığı"
              className="w-44 rounded-lg bg-white/10 px-2 py-1 text-sm text-white outline-none focus:ring-1 focus:ring-indigo-300"
            />
            <button data-testid={`week-edit-save-${week.week_number}`} onClick={saveWeek} className="rounded-lg bg-emerald-500 px-3 py-1 text-xs font-bold text-white transition hover:bg-emerald-600 active:scale-95">Kaydet</button>
            <button data-testid={`week-edit-cancel-${week.week_number}`} onClick={() => setWEditing(false)} className="rounded-lg bg-white/10 px-3 py-1 text-xs font-bold text-white transition hover:bg-white/20 active:scale-95">İptal</button>
          </div>
        ) : (
          <h3 className="font-display text-lg font-bold tracking-wide">{week.month ? `${trUpper(week.month)} · ` : ""}{week.week_number}. HAFTA{week.title ? ` · ${week.title}` : ""}</h3>
        )}
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-emerald-500 px-3 py-1 text-xs font-bold">{done}/{total || 0} tamamlandı</span>
          {isAdmin && !wEditing && (
            <>
              <button data-testid={`edit-week-${week.week_number}`} onClick={() => { setWMonth(week.month || ""); setWTitle(week.title || ""); setWEditing(true); }} className="no-print rounded-lg bg-white/10 p-1.5 text-white transition hover:bg-white/20 active:scale-90" title="Haftayı düzenle">
                <Pencil className="h-4 w-4" />
              </button>
              <button data-testid={`delete-week-${week.week_number}`} onClick={() => onDeleteWeek(week.week_number)} className="no-print rounded-lg bg-rose-500/90 p-1.5 text-white transition hover:bg-rose-600 active:scale-90" title="Haftayı sil">
                <Trash2 className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      </div>
      <div className="overflow-x-auto bg-white">
        <table className="w-full min-w-[900px] border-collapse text-sm">
          <thead>
            <tr className="bg-[#233047] text-xs font-bold tracking-wider text-slate-200">
              <th className="w-44 px-4 py-3 text-left">DERSLER</th>
              {DAYS.map((d) => <th key={d} className="px-2 py-3 text-center">{trUpper(d)}</th>)}
            </tr>
          </thead>
          <tbody>
            {week.rows.map((row, ri) => {
              const c = colorOf(row.color);
              return (
                <tr key={ri} className="border-t border-slate-100">
                  <SubjectCell week={week.week_number} ri={ri} row={row} isAdmin={isAdmin} onSave={onRowSave} onDelete={onRowDelete} />
                  {DAYS.map((day) => {
                    const cell = row.cells?.[day];
                    if (!cell) return <td key={day} className="border-l border-slate-100 text-center text-slate-300">—</td>;
                    const st = STATUS[cell.status] || STATUS.bekliyor;
                    const Icon = st.icon;
                    return (
                      <td key={day} className="border-l border-slate-100 p-1.5 align-top">
                        <div
                          data-testid={`study-cell-${week.week_number}-${ri}-${day.toLowerCase()}`}
                          onClick={() => canEdit && onCell({ week_number: week.week_number, row_index: ri, day, subject: row.subject_name, cell: { ...cell } })}
                          className={`h-full w-full rounded-lg border ${st.cell} p-2 text-center transition ${canEdit ? "cursor-pointer hover:shadow-md hover:ring-2 hover:ring-indigo-200" : ""}`}
                        >
                          <div className="text-[13px] font-bold text-slate-800">{cell.unit}</div>
                          {cell.pages && <div className="mt-0.5 font-mono text-[10px] uppercase tracking-wide text-slate-400">Sayfa {cell.pages}</div>}
                          <div className="mt-0.5 text-[11px] text-slate-500">Hedef {cell.target} soru</div>
                          {cell.filled && (
                            <div className="mt-1 font-mono text-[11px] font-semibold text-slate-700">
                              D {cell.dogru} · Y {cell.yanlis} · B {cell.bos}
                            </div>
                          )}
                          <Icon className={`mx-auto mt-1 h-4 w-4 ${st.iconColor}`} />
                          {canApprove && cell.filled && cell.status !== "onayli" && (
                            <button
                              data-testid={`approve-cell-${week.week_number}-${ri}-${day.toLowerCase()}`}
                              onClick={(e) => { e.stopPropagation(); onApprove(week.week_number, ri, day, cell); }}
                              className="mt-1.5 w-full rounded-md bg-emerald-600 px-2 py-1 text-[11px] font-bold text-white transition hover:bg-emerald-700 active:scale-95 no-print"
                            >
                              ✓ Koç Onayla
                            </button>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SubjectCell({ week, ri, row, isAdmin, onSave, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(row.sublabel || "");
  const save = () => {
    setEditing(false);
    if (val !== (row.sublabel || "")) onSave(week, ri, { sublabel: val });
  };
  return (
    <td className="bg-[#1E293B] px-4 py-4 align-top text-white">
      <div className="flex items-start justify-between gap-1">
        <div className="min-w-0">
          <div className="font-bold">{trUpper(row.subject_name)}</div>
          {editing ? (
            <input
              autoFocus
              data-testid={`row-sublabel-input-${week}-${ri}`}
              value={val}
              onChange={(e) => setVal(e.target.value)}
              onBlur={save}
              onKeyDown={(e) => e.key === "Enter" && save()}
              placeholder="Kaynak / yayınevi"
              className="mt-1 w-36 rounded bg-white/10 px-2 py-1 text-xs text-white outline-none focus:ring-1 focus:ring-indigo-300"
            />
          ) : (
            row.sublabel && <div className="text-xs text-slate-400">{row.sublabel}</div>
          )}
        </div>
        {isAdmin && (
          <div className="flex flex-col gap-1 no-print">
            <button
              data-testid={`row-edit-${week}-${ri}`}
              onClick={() => { setVal(row.sublabel || ""); setEditing(true); }}
              className="rounded p-1 text-slate-400 transition hover:bg-white/10 hover:text-white"
              title="Kaynak adını düzenle"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              data-testid={`row-del-${week}-${ri}`}
              onClick={() => onDelete(week, ri)}
              className="rounded p-1 text-slate-400 transition hover:bg-white/10 hover:text-rose-300"
              title="Satırı sil"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
    </td>
  );
}

function Legend() {
  return (
    <div className="flex items-center gap-3 text-xs font-semibold text-slate-600">
      {Object.entries(STATUS).map(([k, v]) => (
        <span key={k} className="flex items-center gap-1.5">
          <span className={`h-3.5 w-3.5 rounded border ${v.chip}`} /> {v.label}
        </span>
      ))}
    </div>
  );
}

function NumField({ label, value, onChange, color, testid }) {
  return (
    <div>
      <label className={`mb-1 block text-center text-xs font-bold uppercase ${color}`}>{label}</label>
      <input
        data-testid={testid}
        type="number"
        min={0}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-slate-200 bg-white px-2 py-2.5 text-center text-lg font-bold outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
      />
    </div>
  );
}
